import type { Rule } from "antd/es/form";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { isCashbackMaxBelowMinLoss } from "./cashbackAuthoring";
import { RESULT_PARAM_SCHEMA, type ResultParamField } from "./constants";

/**
 * Percent-of-trigger sizing state for a step's result params (BONENG-913).
 * `eligible` = flag on + the step's trigger event carries a monetary amount;
 * `percentSet` = the operator has filled the percent input.
 *
 * `percentRequired` (BONENG-1171) is set when the operator picked percent
 * sizing from a mode selector that hides the fixed-amount input: the percent
 * carries the requiredness the hidden `amount` field would otherwise hold,
 * so an empty reward can't pass validation through a field nobody can see.
 */
export type PercentOfTriggerContext = {
  eligible: boolean;
  percentSet: boolean;
  percentRequired?: boolean;
};

/**
 * BONENG-991: cross-field context for the `positive_amount` validator.
 * Prevents authoring impossible-window bonuses (floor above what the bet
 * can actually reach):
 * - `perPickAmount`: free-pick award value after split (floor must not exceed it).
 * - `maxWagerLimit`: Wager Cap on show_bonus / profit_boost (floor must not exceed it).
 * Skipped when `percentOfTrigger` is true (value unknown at authoring time).
 */
export type PositiveAmountContext = {
  perPickAmount: number | null;
  maxWagerLimit: number | null;
  percentOfTrigger: boolean;
};

export type CashbackCapContext = {
  minLossThreshold: number | null;
};

/**
 * Sibling result_param values for conditional rules (e.g. ThreeOaks
 * ``value`` required when ``provider === "threeoaks"``).
 */
export type SiblingParamsContext = Record<string, unknown>;

/**
 * Parse an authoring timestamp as an instant.
 * Offset/Z ISO strings are absolute; datetime-local (`YYYY-MM-DDTHH:mm`) is
 * the operator's local wall clock (same convention as DateRangeField).
 */
export const parseAuthoringDateMs = (value: string): number => {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return Date.parse(`${trimmed}:00`);
  }
  return Date.parse(trimmed);
};

export const siblingConditionMatches = (
  when: { key: string; equals: string } | undefined,
  siblings?: SiblingParamsContext,
): boolean =>
  Boolean(when && String(siblings?.[when.key] ?? "") === when.equals);

const POSITIVE_AMOUNT_FLOOR_RESULT_TYPES = new Set([
  "award_free_picks",
  "show_bonus",
  "create_profit_boost_promotion",
]);

/**
 * Form paths to re-validate when positive_amount cross-field inputs change
 * (min wager vs wager cap / per-pick award). BONENG-1263.
 */
export const positiveAmountCrossFieldValidatePaths = (
  stepFieldName: number,
  resultTypeKey: string | undefined,
  resultParams: Record<string, unknown> | undefined,
): (string | number)[][] => {
  if (!resultTypeKey || !POSITIVE_AMOUNT_FLOOR_RESULT_TYPES.has(resultTypeKey)) {
    return [];
  }
  if (!resultParams || isBlankParamValue(resultParams.min_bet_amount)) {
    return [];
  }
  return [["steps", stepFieldName, "result_params", "min_bet_amount"]];
};

/**
 * Sibling result_param keys that should re-trigger antd Form.Item rules when
 * a field uses ``requiredWhen`` and/or ``visibleWhen``. Registers both when
 * both are present (they may point at different siblings).
 */
export const conditionalParamDependencyKeys = (
  param: ResultParamField,
): string[] => {
  const keys: string[] = [];
  if (param.requiredWhen) keys.push(param.requiredWhen.key);
  if (
    param.visibleWhen &&
    param.visibleWhen.key !== param.requiredWhen?.key
  ) {
    keys.push(param.visibleWhen.key);
  }
  if (param.format === "not_before_settlement") {
    keys.push("market_settlement_date");
  }
  return keys;
};

const isBlankParamValue = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

/**
 * antd form values arrive as strings; reject non-numeric text before range checks
 * so operators see a format error instead of a misleading bound message (BONENG-1248).
 */
const rejectUnlessFiniteNumber = (
  label: string,
  value: unknown,
): number | Promise<void> => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return Promise.reject(new Error(`${label} must be a valid number`));
  }
  return num;
};

/**
 * Drop params gated by ``requiredWhen`` / ``visibleWhen`` when the sibling
 * condition is inactive (e.g. ThreeOaks ``value``/``type`` while provider
 * is not threeoaks).
 */
export const stripInactiveConditionalResultParams = (
  resultTypeKey: string,
  params: Record<string, unknown>,
): Record<string, unknown> => {
  const schema = RESULT_PARAM_SCHEMA[resultTypeKey] ?? [];
  if (schema.length === 0) return params;
  const next = { ...params };
  for (const param of schema) {
    if (!param.requiredWhen && !param.visibleWhen) continue;
    const active =
      siblingConditionMatches(param.requiredWhen, next) ||
      siblingConditionMatches(param.visibleWhen, next);
    if (!active) delete next[param.key];
  }
  return next;
};

/**
 * antd rules for a result param input. `min`/`max` bounds (whole numbers,
 * e.g. split_count 1–25, BONENG-883), `format: "american_odds"`
 * (show_bonus min_odds, BONENG-896), and `format: "percent"` plus the
 * percent/$ mutual exclusion (BONENG-913) are enforced here so the operator
 * sees a field error at authoring time instead of the backend's 422 toast.
 */
export const paramFieldRules = (
  param: ResultParamField,
  percentContext?: PercentOfTriggerContext,
  positiveAmountContext?: PositiveAmountContext,
  siblingParams?: SiblingParamsContext,
  cashbackCapContext?: CashbackCapContext,
): Rule[] => {
  // While a percent is filled in, the $ field it replaces is no longer
  // required — and must stay blank (the backend rejects configs carrying
  // both, "% INSTEAD OF a $ amount").
  const percentReplacesThisParam = Boolean(
    param.replacedByPercent && percentContext?.eligible && percentContext.percentSet,
  );
  const requiredBySibling = siblingConditionMatches(
    param.requiredWhen,
    siblingParams,
  );
  const requiredAsPercentSizing = Boolean(
    param.percentOfTrigger && percentContext?.percentRequired,
  );
  const rules: Rule[] =
    (param.required || requiredBySibling || requiredAsPercentSizing) &&
    !percentReplacesThisParam
      ? [{ required: true, message: `${param.label} is required` }]
      : [];
  if (percentReplacesThisParam) {
    rules.push({
      validator: (_rule, value) => {
        if (value === undefined || value === null || value === "") {
          return Promise.resolve();
        }
        return Promise.reject(
          new Error(
            `Leave ${param.label} blank while "% of trigger amount" is set — the reward uses exactly one`,
          ),
        );
      },
    });
  }
  if (param.min !== undefined || param.max !== undefined) {
    const { min, max } = param;
    const bounds =
      min !== undefined && max !== undefined
        ? `between ${min} and ${max}`
        : min !== undefined
          ? `of at least ${min}`
          : `of at most ${max}`;
    rules.push({
      validator: (_rule, value) => {
        if (isBlankParamValue(value)) {
          return Promise.resolve();
        }
        const parsed = rejectUnlessFiniteNumber(param.label, value);
        if (parsed instanceof Promise) return parsed;
        const num = parsed;
        if (
          !Number.isInteger(num) ||
          (min !== undefined && num < min) ||
          (max !== undefined && num > max)
        ) {
          return Promise.reject(
            new Error(`${param.label} must be a whole number ${bounds}`),
          );
        }
        return Promise.resolve();
      },
    });
  }
  if (param.format === "american_odds") {
    rules.push({
      validator: (_rule, value) => {
        if (isBlankParamValue(value)) {
          return Promise.resolve();
        }
        const parsed = rejectUnlessFiniteNumber(param.label, value);
        if (parsed instanceof Promise) return parsed;
        const num = parsed;
        // Valid American price: whole number with |value| >= 100 (e.g. 150, -110).
        // Rejects decimals (2.5), 0, and the -99..99 band (50, -75).
        if (!Number.isInteger(num) || Math.abs(num) < 100) {
          return Promise.reject(
            new Error(
              `${param.label} must be American odds — a whole number of at least +100 or at most -100 (e.g. 150 or -110)`,
            ),
          );
        }
        return Promise.resolve();
      },
    });
  }
  if (param.format === "positive_amount") {
    rules.push({
      validator: (_rule, value) => {
        if (isBlankParamValue(value)) {
          return Promise.resolve();
        }
        const parsed = rejectUnlessFiniteNumber(param.label, value);
        if (parsed instanceof Promise) return parsed;
        const num = parsed;
        if (num <= 0) {
          return Promise.reject(
            new Error(`${param.label} must be an amount greater than 0`),
          );
        }
        const ctx = positiveAmountContext;
        if (ctx && !ctx.percentOfTrigger && param.key === "min_bet_amount") {
          if (
            ctx.perPickAmount !== null &&
            num > ctx.perPickAmount
          ) {
            return Promise.reject(
              new Error(
                `${param.label} ($${num.toFixed(2)}) exceeds the per-pick award value ($${ctx.perPickAmount.toFixed(2)}). The pick would be unusable.`,
              ),
            );
          }
          if (
            ctx.maxWagerLimit !== null &&
            num > ctx.maxWagerLimit
          ) {
            return Promise.reject(
              new Error(
                `${param.label} ($${num.toFixed(2)}) exceeds the ${AppStrings.PAM_DYNAMIC_BONUS_PARAM_WAGER_CAP} ($${ctx.maxWagerLimit.toFixed(2)}) — no wager could satisfy both.`,
              ),
            );
          }
        }
        if (ctx && !ctx.percentOfTrigger && param.key === "max_wager_limit") {
          const rawMinBet = siblingParams?.min_bet_amount;
          if (!isBlankParamValue(rawMinBet)) {
            const minBetNum = Number(rawMinBet);
            if (Number.isFinite(minBetNum) && minBetNum > 0 && num < minBetNum) {
              return Promise.reject(
                new Error(
                  `${AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_BET_AMOUNT} ($${minBetNum.toFixed(2)}) exceeds the ${param.label} ($${num.toFixed(2)}) — no wager could satisfy both.`,
                ),
              );
            }
          }
        }
        return Promise.resolve();
      },
    });
  }
  if (param.format === "percent") {
    rules.push({
      validator: (_rule, value) => {
        if (isBlankParamValue(value)) {
          return Promise.resolve();
        }
        const parsed = rejectUnlessFiniteNumber(param.label, value);
        if (parsed instanceof Promise) return parsed;
        const num = parsed;
        // Mirrors the backend guard: a number greater than 0 and at most
        // 100 (decimals like 12.5 allowed).
        if (num <= 0 || num > 100) {
          return Promise.reject(
            new Error(
              `${param.label} must be a number greater than 0 and at most 100`,
            ),
          );
        }
        return Promise.resolve();
      },
    });
  }
  if (param.format === "money") {
    rules.push({
      validator: (_rule, value) => {
        if (isBlankParamValue(value)) {
          return Promise.resolve();
        }
        const parsed = rejectUnlessFiniteNumber(param.label, value);
        if (parsed instanceof Promise) return parsed;
        const num = parsed;
        // Mirrors the backend guard (BONENG-976): a positive dollar amount
        // of at least 0.01 — sub-cent values round down to $0 at runtime.
        if (num < 0.01) {
          return Promise.reject(
            new Error(
              `${param.label} must be a positive amount of at least 0.01`,
            ),
          );
        }
        return Promise.resolve();
      },
    });
  }
  if (param.key === "max_cashback_amount" && cashbackCapContext) {
    rules.push({
      validator: (_rule, value) => {
        if (
          isCashbackMaxBelowMinLoss(cashbackCapContext.minLossThreshold, value)
        ) {
          return Promise.reject(
            new Error(AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_MAX_BELOW_MIN),
          );
        }
        return Promise.resolve();
      },
    });
  }
  if (param.format === "not_before_settlement") {
    rules.push({
      validator: (_rule, value) => {
        if (value === undefined || value === null || value === "") {
          return Promise.resolve();
        }
        const settlement = String(siblingParams?.market_settlement_date ?? "");
        if (!settlement) {
          return Promise.reject(
            new Error(AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_EXPIRY_NO_TRAIT),
          );
        }
        const expiryMs = parseAuthoringDateMs(String(value));
        const settlementMs = parseAuthoringDateMs(settlement);
        if (!Number.isFinite(expiryMs) || !Number.isFinite(settlementMs)) {
          return Promise.reject(
            new Error(AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_EXPIRY_INVALID),
          );
        }
        if (expiryMs < settlementMs) {
          return Promise.reject(
            new Error(AppStrings.PAM_DYNAMIC_BONUS_FUTURE_BONUS_EXPIRY_ERROR),
          );
        }
        return Promise.resolve();
      },
    });
  }
  return rules;
};
